import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { TIMELINE_HOURS, TIMELINE_START_HOUR, TIMELINE_END_HOUR, TIMELINE_DURATION_MINUTES } from './timelineConstants'
import { AppointmentBlock } from './AppointmentBlock'
import { GhostAppointmentBlock } from './GhostAppointmentBlock'
import { BreakBlock } from './BreakBlock'
import { TimeManagementBlock } from './TimeManagementBlock'
import { TaskBlock, TaskGhostBlock } from './TaskBlock'
import { useTimelineTrackMouse } from '../model/useTimelineTrackMouse'
import { useTimelineRowData } from '../model/TimelineRowDataContext'
import { useBreakEditContext } from '../../../features/break-edit'
import { useTaskRescheduleContext } from '../../../features/task-reschedule'
import { useDispositionInteraction } from '../../../features/disposition'
import { useAppointmentEditContext } from '../../../features/appointment-edit'
import { useDispositionConfig } from '../../../features/disposition'
import { useTimelineConfig } from '../model/TimelineConfigContext'
import { detectAppointmentTransferOverlaps, detectTaskTransferOverlaps } from '../../../shared/lib/timeline/detectTransferOverlaps'
import { routes } from '../../../shared/config/routes'

export interface GhostBlockInfo {
  anfahrtStartIso: string
  travelDuration: number
  workDuration: number
  departureDuration: number
  committed?: boolean
  isColliding?: boolean
}

export const TimelineTrack = () => {
  const {
    getDraftStart,
    getDraftMove,
    getDraftCommit,
    getDraftCancel,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
    onBlockMove,
    onBlockMoveEnd,
    onBlockMoveToRow,
  } = useDispositionInteraction()

  const { appointmentId: editingAppointmentId } = useAppointmentEditContext()
  const { ghostColor } = useDispositionConfig()
  const { isReadonly, editingTaskId, onTaskClick, onAppointmentClick, canRescheduleCompleted } = useTimelineConfig()

  const { getBreakDragStart } = useBreakEditContext()
  const { taskDragState, startTaskDrag } = useTaskRescheduleContext()

  const navigate = useNavigate()
  const onTimeManagementClick = useCallback(
    (id: number) => navigate(routes.zeitmanagement.edit(id)),
    [navigate],
  )

  const {
    technicianId,
    technicianName,
    appointments,
    timeManagementEntries,
    breakBlock,
    originalBreakBlock,
    ghostBlock,
    isWorkingDay,
    shiftStartHour,
    shiftEndHour,
    timedTasks,
    dayIso,
    laneMap,
  } = useTimelineRowData()

  const {
    trackRef,
    getIsoFromMouseEvent,
    handleResizeStart,
    handleBlockDragStart,
    isInteracting,
  } = useTimelineTrackMouse({
    anfahrtStartIso: ghostBlock?.anfahrtStartIso,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
    onBlockMove,
    onBlockMoveEnd,
    onBlockMoveToRow,
    dayIso,
  })

  const onDraftStart = getDraftStart(technicianId)
  const onDraftMove = getDraftMove(technicianId)
  const onDraftCommit = getDraftCommit(technicianId, technicianName)
  const onBreakDragStart = getBreakDragStart(technicianId)

  const isInWorkingHours = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isWorkingDay) return false
      if (shiftStartHour == null || shiftEndHour == null) return true
      const rect = e.currentTarget.getBoundingClientRect()
      const xPercent = ((e.clientX - rect.left) / rect.width) * 100
      const minutesFromStart = (xPercent / 100) * TIMELINE_DURATION_MINUTES
      const hour = TIMELINE_START_HOUR + minutesFromStart / 60
      return hour >= shiftStartHour && hour < shiftEndHour
    },
    [isWorkingDay, shiftStartHour, shiftEndHour],
  )

  const { transferTravelTasks, transferDepartureTasks } = useMemo(
    () => detectTaskTransferOverlaps(timedTasks, editingTaskId),
    [timedTasks, editingTaskId],
  )

  const { transferTravel, transferDeparture } = useMemo(
    () => detectAppointmentTransferOverlaps(appointments, editingAppointmentId),
    [appointments, editingAppointmentId],
  )

  return (
    <div
      ref={trackRef}
      data-timeline-track
      data-technician-id={technicianId}
      className={`flex-1 min-w-0 self-stretch relative bg-white outline outline-1 outline-offset-[-0.50px] outline-slate-200 overflow-hidden ${isReadonly ? 'cursor-default' : 'cursor-crosshair'}`}
      onMouseDown={isReadonly ? undefined : (e) => {
        if (isInteracting()) return
        if (!isInWorkingHours(e)) return
        onDraftStart(getIsoFromMouseEvent(e))
      }}
      onMouseMove={isReadonly ? undefined : (e) => {
        if (isInteracting()) return
        if (ghostBlock && !ghostBlock.committed) onDraftMove(getIsoFromMouseEvent(e))
      }}
      onMouseUp={isReadonly ? undefined : () => {
        if (isInteracting()) return
        if (ghostBlock && !ghostBlock.committed) onDraftCommit()
      }}
      onMouseLeave={isReadonly ? undefined : () => {
        if (isInteracting()) return
        if (!ghostBlock?.committed) getDraftCancel()
      }}
    >
      <div className="absolute inset-0 flex pointer-events-none">
        {TIMELINE_HOURS.map((hour: number) => (
          <div key={hour} className="flex-1 border-r border-slate-100 last:border-r-0" />
        ))}
      </div>
      {!isWorkingDay ? (
        <div className="absolute inset-0 bg-gray-300/60 pointer-events-none" />
      ) : (
        <>
          {shiftStartHour != null && shiftStartHour > TIMELINE_START_HOUR && (
            <div
              className="absolute top-0 bottom-0 bg-gray-300/60 pointer-events-none"
              style={{
                left: 0,
                width: `${((shiftStartHour - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100}%`,
              }}
            />
          )}
          {shiftEndHour != null && shiftEndHour < TIMELINE_END_HOUR && (
            <div
              className="absolute top-0 bottom-0 bg-gray-300/60 pointer-events-none"
              style={{
                left: `${((shiftEndHour - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100}%`,
                right: 0,
              }}
            />
          )}
        </>
      )}
      {originalBreakBlock && (
        <BreakBlock
          startTimeIso={originalBreakBlock.startTimeIso}
          className="opacity-30 pointer-events-none"
        />
      )}
      {breakBlock && (() => {
        const lane = laneMap.get('br')
        return (
          <BreakBlock
            startTimeIso={breakBlock.startTimeIso}
            onMouseDown={isReadonly ? undefined : (e) => {
              e.stopPropagation()
              onBreakDragStart(e)
            }}
            laneIndex={lane?.laneIndex}
            laneCount={lane?.laneCount}
          />
        )
      })()}
      {timeManagementEntries.map((entry) => {
        const lane = laneMap.get(`tm:${entry.id}`)
        return (
          <TimeManagementBlock
            key={entry.id}
            entry={entry}
            dayIso={dayIso}
            shiftStartHour={shiftStartHour}
            shiftEndHour={shiftEndHour}
            onClick={onTimeManagementClick}
            laneIndex={lane?.laneIndex}
            laneCount={lane?.laneCount}
          />
        )
      })}
      {appointments.map((appt) => {
        const lane = laneMap.get(`a:${appt.id}`)
        return (
          <AppointmentBlock
            key={appt.id}
            appointment={appt}
            isEditing={appt.id === editingAppointmentId}
            isTransferTravel={transferTravel.has(appt.id)}
            isTransferDeparture={transferDeparture.has(appt.id)}
            onClick={appt.id !== editingAppointmentId && onAppointmentClick ? () => onAppointmentClick(appt.id) : undefined}
            laneIndex={lane?.laneIndex}
            laneCount={lane?.laneCount}
          />
        )
      })}
      {timedTasks.map((task) => {
        const isCompletedTask = task.status === 'COMPLETED'
        const dragAllowed =
          !isReadonly &&
          task.id !== editingTaskId &&
          (!isCompletedTask || canRescheduleCompleted)
        const lane = laneMap.get(`t:${task.id}`)
        return (
          <TaskBlock
            key={task.id}
            task={task}
            isDragging={taskDragState?.taskId === task.id}
            isEditing={task.id === editingTaskId}
            onDragStart={dragAllowed ? (e) => startTaskDrag(task, technicianId, e) : undefined}
            onClick={task.id !== editingTaskId && onTaskClick ? () => onTaskClick(task.id) : undefined}
            isTransferTravel={transferTravelTasks.has(task.id)}
            isTransferDeparture={transferDepartureTasks.has(task.id)}
            laneIndex={lane?.laneIndex}
            laneCount={lane?.laneCount}
          />
        )
      })}
      {taskDragState?.currentTechnicianId === technicianId && !taskDragState.isDayLevel && (
        <TaskGhostBlock
          anfahrtStartIso={taskDragState.anfahrtStartIso}
          durations={taskDragState.durations}
          isColliding={taskDragState.isColliding}
        />
      )}
      {!isReadonly && ghostBlock && (
        <GhostAppointmentBlock
          anfahrtStartIso={ghostBlock.anfahrtStartIso}
          travelDuration={ghostBlock.travelDuration}
          workDuration={ghostBlock.workDuration}
          departureDuration={ghostBlock.departureDuration}
          committed={ghostBlock.committed}
          color={ghostColor}
          isColliding={ghostBlock.isColliding}
          onResizeStart={handleResizeStart}
          onBlockDragStart={handleBlockDragStart}
        />
      )}
    </div>
  )
}
